import { useTranslation } from "react-i18next"
import ButtonSubAction from "../../../../../components/buttons/ButtonSubAction"
import { DefaultMealImageRiceMeal } from "../../../../../components/buttons/IconButtons"
import CustomChip from "../../../../../components/CustomChip"
import CustomFullRowCard from "../../../../../components/CustomFullRowCard"
import { colors } from "../../../../../enums/colors"
import renderAllergenIcons from "../../../Dining/Dishes/SubComponents/AllergenIcons"
import { MealTypeEnum } from "../../../Dining/Menu/Model/Model"
import {currencyFormat} from '../../../../../Apis/functions';


/**
 * @author      Jhovanny Fragoso Marquez
 * @jobTitle    Frontend Developer
 * @email       jfragoso@ibl.mx
 *
 * @description
 * DishResume is a React component that renders a summary card for a selected dish within an order.
 * It displays the dish's name, image (or a default image if not present), description, meal type, allergens,
 * and provides an action button to remove the dish from the order.
 *
 * @component
 * @param {Object}   props
 * @param {Object}   props.info                  - The object containing all relevant info about the dish and its meal type.
 * @param {number}   props.info.id               - Unique identifier for the dish-in-mealtime instance.
 * @param {Object}   props.info.foodieItem       - The dish object containing all details (name, description, image, allergens, etc).
 * @param {number}   props.info.foodieMealTypeId - Identifier of the meal type for this dish (matches keys in MealTypeEnum).
 *
 * @returns {JSX.Element} The rendered summary card for the dish, including image, name, description, allergens, meal type, and a remove button.
 *
 * @example
 * <DishResume info={dishSummaryObject} />
 */


const DishResume = ({ info, handleSelection, foodieSetting }) => {
    const dish = info?.foodieItem
    const { t } = useTranslation()
    const mealTypeTranslation = t(MealTypeEnum[info?.foodieMealTypeId].name)


    return (
        <div>
            <CustomFullRowCard
                actionLeft={<p className="text-capitalize mb-0">{dish?.name}</p>}
                className="position-relative mb-3"
                btnActions={
                    <ButtonSubAction
                        id="btn_dr_remove"
                        name="remove"
                        onClick={() => handleSelection(info.id)}
                        className="btn-href-general-950"
                    />
                }
            >
                <CustomChip
                    background={colors.secondary100}
                    borderColor={colors.secondary150}
                    fontColor={colors.secondary700}
                    legend={mealTypeTranslation}
                    className="position-absolute r-16px t-16px mb-2 mb-md-0  top-2 right-2"
                />

                <div className="row g-3 align-items-start w-100">
                    <div className="col-12 col-md-3">
                        {dish.image ? (
                            <div className="ratio ratio-4x3">
                                <img
                                    src={`data:image/png;base64,${dish.image}`}
                                    alt="dishimage"
                                    className="img-fluid rounded w-100 h-100 d-block object-fit-cover"
                                />
                            </div>
                        ) : (
                            <div className="ratio ratio-4x3 bg-light rounded d-flex justify-content-center align-items-center">
                                <DefaultMealImageRiceMeal />
                            </div>
                        )}
                    </div>

                    <div className="col-12 col-md-9">
                        <div className="px-1">
                            <p className="mb-2 clamp-3">{dish?.description}</p>

                            {!foodieSetting?.isBaseCost && !foodieSetting?.isMealTimeCost && (
                                <div className="mb-2">
                                    <div className="fs-12px ibl-font-w-600 font-color-general-950 pt-2">{t('cost')}</div>
                                    <div className="fs-14px">{"$ " + currencyFormat(info?.cost)}</div>
                                </div>
                            )}

                            <p className="ibl-font-w-600 mb-2">{t('allergens_and_diet_type')}</p>
                            <div className="d-flex flex-wrap gap-2">
                                {renderAllergenIcons(dish, t)}
                            </div>
                        </div>
                    </div>
                </div>
            </CustomFullRowCard>

        </div>
    )
}

export default DishResume
